import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

export class IdName {
  id: string;
  name: string;
  disabled?: boolean;
}
@Component({
  selector: 'ngx-autocomplete-input',
  templateUrl: 'autocomplete-input.component.html',
  styleUrls: [ 'autocomplete-input.component.scss' ]
})
export class AutocompleteInputComponent {
  @ViewChild(NgSelectComponent, { static: false }) ngSelect: any; 
  @Input() items: IdName[] = [];
  @Input() initialValue: IdName | IdName[];
  // @Input() initialValues: IdName[] = [];
  @Input() addTag: boolean;
  @Input() addTagText: string;
  @Input() placeholder: string;
  @Input() multiple: boolean = false;
  @Input() readonly: boolean;
  @Input() debounceTime: number = 300;
  @Input() showTooltip: boolean = false;
  @Input() clearable: boolean = true;
  @Input() searchByField: string = 'name';
  @Input() displayField: string = 'name';
  @Input() loadingItems: boolean = false;
  @Input() disabled: boolean = false;
  @Input() appendTo: string = null;
  @Input() preventArrowsEvent: boolean = false;
  @Input() tooltipPlacement: string = 'right';
  @Input() virtualScroll = true;
  @Output() focused = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();
  @Output() open = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() itemSelected: EventEmitter<IdName> = new EventEmitter<IdName>();
  @Output() itemsSelected: EventEmitter<IdName[]> = new EventEmitter<IdName[]>();
  @Output() scrollToEnd: EventEmitter<undefined> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  @Output() searchDebounced: EventEmitter<string> = new EventEmitter<string>();
  @Output() dropdownClosed: EventEmitter<void> = new EventEmitter<void>();
  searchSubject = new Subject<string>();
  searchTerm: string = '';

  constructor(
    private sanitizer: DomSanitizer,
  ) {
    this.searchSubject.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    ).subscribe(search => {
      this.searchDebounced.emit(search);
    })
  }

  trackByFn(item: any): any {
    return item ? item.id : undefined;
  }
  
  selectItems(data: any) {
    if (this.multiple) {
      this.itemsSelected.emit(data);
    } else {
      this.itemSelected.emit(data);
    }
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.searchSubject.next(term);
    this.search.emit(term);
  }

  onDropdownClose() {
    this.dropdownClosed.emit();
  }

  endReached() {
    this.scrollToEnd.emit()
  }

  keyDownFn = (event: KeyboardEvent) => {
    if (!this.preventArrowsEvent) return true;

    const isOpen = !!this.ngSelect?.isOpen;

    switch (event?.code) {
      case 'Enter':
        return true;
      case 'ArrowDown':
      case 'ArrowUp':
        return isOpen;
      default:
        return false;
    }
  };

  compareItems = (o1: any, o2: any) => o1 && o2 && o1.id === o2.id;

  highlightText(text: string): SafeHtml {
    if (!this.searchTerm) return text;

    const escapedSearch = this.searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(escapedSearch, 'gi');
    const highlightedText = text.replace(regex, (match) => `<mark>${match}</mark>`);
    
    return this.sanitizer.bypassSecurityTrustHtml(highlightedText);
  }

  searchFn = (term: string, item:any): boolean  => {
    return item[this.searchByField].toLowerCase().includes(term.toLowerCase());;
  }

  public clear(): void {
    this.initialValue = this.multiple ? [] as IdName[] : null;
    this.searchTerm = '';
    this.ngSelect?.clearModel();
    if (this.multiple) {
      this.itemsSelected.emit([]);
    } else {
      this.itemSelected.emit(null);
    }
  }

  public selectItem(item: IdName): void {
    if (!item) return;
    this.initialValue = item.id as any;
    this.searchTerm = '';
  }

  public focus() {
    setTimeout(() => {
      this.ngSelect.focus();
    });
  }
}
